/**
 * Who a piece of work on this device belongs to: a person in a shop (MAP 225b).
 *
 * Not the role. The server enforces roles and dedupes per shop (`(org_id, client_id,
 * mutation_id)`), so a write replayed under another shop is NOT deduped and must not be
 * sent there, while the same person demoted in the same shop is still the person who made
 * it. Every offline store compares accounts through this file, so the three of them
 * (queued writes, photos, refused writes) cannot disagree about whose a row is.
 */
export type AccountRef = Readonly<{ userId: string; orgId: string }>;

/** Just the person and the shop, whatever else the value carries (a `Session`, a row). */
export function accountRef(
  a: { userId: string; orgId: string } | null | undefined,
): AccountRef | null {
  return a ? { userId: a.userId, orgId: a.orgId } : null;
}

/**
 * The same person in the same shop. Nobody is never the same as anybody, nobody included:
 * a write made with no account belongs to no account.
 */
export function sameAccount(
  a: { userId: string; orgId: string } | null | undefined,
  b: { userId: string; orgId: string } | null | undefined,
): boolean {
  return !!a && !!b && a.userId === b.userId && a.orgId === b.orgId;
}

/**
 * Has the account changed? The same person in the same shop, or nobody both times. The one
 * answer to that question for every store that is told whose session it runs under.
 */
export function sameAccountOrNobody(
  a: { userId: string; orgId: string } | null | undefined,
  b: { userId: string; orgId: string } | null | undefined,
): boolean {
  return (!a && !b) || sameAccount(a, b);
}

/** A string for the pair, or null for nobody: for logs, keys and lock names. */
export function accountRefKey(a: { userId: string; orgId: string } | null | undefined) {
  return a ? `${a.userId}|${a.orgId}` : null;
}

/** An owner read back from disk, which is untrusted storage: anything malformed is none. */
export function parseAccountRef(raw: unknown): AccountRef | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.userId !== 'string' || typeof a.orgId !== 'string') return null;
  if (!a.userId || !a.orgId) return null;
  return { userId: a.userId, orgId: a.orgId };
}
